import type { CSSProperties, ReactNode } from 'react';
import { Ikon } from '../Ikon.tsx';

/**
 * The primitives the widget and the portal are built from. Everything visual lives in
 * styles.css and comes from tokens — no hex, no px, no font size
 * here (CLAUDE.md, «Styling»). The minimum touch target is `--innspel-layout-touch`;
 * it also applies to mouse and keyboard on the web.
 *
 * `ikon` is always an IONICONS NAME, never an emoji (brand-ui-guidelines,
 * UX review C3). The names are in `Ikon.tsx`, and an unknown name throws.
 */

/** The token name from theme.ts (camelCase) → the CSS variable from tokens.css. Same as the panel's. */
export function tokenVar(navn: string): string {
  return `var(--innspel-color-${navn.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)})`;
}

/**
 * `tonet` is the brand colour on its own surface (`brandBg`/`brand`), without a border — the same
 * name and the same shape as the widget's variant. It is used where a choice is an ACTION without
 * being the screen's primary action: «Ja, jeg også» is one of three equal answers to a known
 * issue, and filled in the action colour it became the screen's one choice (DD-63, clarified
 * 2026-09-20; the wireframe `innsending-web.jsx` draws the three answers that way).
 */
type Variant = 'primary' | 'tonet' | 'secondary' | 'ghost' | 'danger';

export function Btn({
  label,
  onClick,
  variant = 'primary',
  ikon,
  hint,
  disabled,
  ariaLabel,
  grow,
  tall,
  type = 'button',
  testId,
  autoFocus,
  busy,
}: {
  label: string;
  onClick?: () => void;
  variant?: Variant;
  ikon?: string;
  hint?: string;
  disabled?: boolean;
  /** The action is in progress: `aria-busy`, so the screen reader hears that the button is working, not just that it is locked. */
  busy?: boolean;
  ariaLabel?: string;
  grow?: boolean;
  tall?: boolean;
  type?: 'button' | 'submit';
  testId?: string;
  autoFocus?: boolean;
}) {
  const hintId = hint ? `innspel-hint-${slug(testId ?? label)}` : undefined;
  return (
    <div className="innspel-knapp-ramme" data-grow={grow ? 'true' : undefined}>
      <button
        type={type}
        className="innspel-knapp"
        data-variant={variant}
        data-tall={tall ? 'true' : undefined}
        data-testid={testId}
        disabled={disabled}
        aria-busy={busy || undefined}
        aria-label={ariaLabel}
        aria-describedby={hintId}
        onClick={onClick}
        autoFocus={autoFocus}
      >
        {ikon ? <Ikon navn={ikon} /> : null}
        {label}
      </button>
      {/* DD-67 på web (R7.3): konsekvensen av å trykke er beslutningsgrunnlag — `body`. */}
      {hint ? (
        <p className="innspel-hjelp" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * An action without a surface and without a border — the widget's `Tekstknapp` on the web. The
 * touch target is `touch` even though the text is shorter, so it is as easy to hit as a button
 * with a surface.
 *
 * The colour is `actionText`, the brand colour (DD-58, clarified 2026-09-20): without a surface
 * and without a border the colour is ALL that separates the button from the text around it.
 * `actionSecondaryText` is identical to `textPrimary` in the light palette and would read as
 * body text.
 */
export function Tekstknapp({
  label,
  ikon,
  onClick,
  disabled,
  ariaLabel,
  testId,
}: {
  label: string;
  ikon?: string;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  testId?: string;
}) {
  return (
    <button type="button" className="innspel-tekstknapp" data-testid={testId} disabled={disabled} aria-label={ariaLabel} onClick={onClick}>
      {ikon ? <Ikon navn={ikon} /> : null}
      {label}
    </button>
  );
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function H({ children, sub, id, nivaa = 2 }: { children: string; sub?: string; id?: string; nivaa?: 1 | 2 }) {
  return (
    <div className="innspel-h">
      {nivaa === 1 ? <h1 id={id}>{children}</h1> : <h2 id={id}>{children}</h2>}
      {/* Undertittelen bærer løftet skjermen gir — `body`, ikke grått 13 px (DD-67). */}
      {sub ? <p className="innspel-hjelp">{sub}</p> : null}
    </div>
  );
}

export function Hint({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <p className="innspel-hint" id={id}>
      {children}
    </p>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="innspel-p">{children}</p>;
}

export function Card({
  children,
  dashed,
  testId,
  role,
  ariaLabel,
}: {
  children: ReactNode;
  dashed?: boolean;
  testId?: string;
  role?: string;
  ariaLabel?: string;
}) {
  return (
    <div className="innspel-kort" data-stiplet={dashed ? 'true' : undefined} data-testid={testId} role={role} aria-label={ariaLabel}>
      {children}
    </div>
  );
}

/**
 * Status as icon + label. The colour is an ADDITION, never the carrier: two of
 * the palette's status colours are measured at under 1.5 in mutual contrast (DD-08).
 */
export function StatusPill({ ikon, etikett, fg, bg }: { ikon: string; etikett: string; fg: string; bg: string }) {
  // Samme mekanikk som panelets Pille: tokennavnet slås opp i den aktive
  // paletten, så mappa i theme.ts virker i lys og mørk uten en variant per farge.
  const style = { '--innspel-pille-fg': tokenVar(fg), '--innspel-pille-bg': tokenVar(bg) } as CSSProperties;
  return (
    <span className="innspel-pille" style={style}>
      <Ikon navn={ikon} />
      {etikett}
    </span>
  );
}

/** A notice. `live` makes it a live region; the receipt and errors use that (DD-47). */
export function Banner({
  ikon,
  tone,
  children,
  live,
  testId,
}: {
  ikon: string;
  tone: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
  live?: boolean;
  testId?: string;
}) {
  return (
    <div className="innspel-banner" data-tone={tone} role={live ? 'status' : undefined} aria-live={live ? 'polite' : undefined} data-testid={testId}>
      <Ikon navn={ikon} />
      <p>{children}</p>
    </div>
  );
}

export function Skeleton({ hoy }: { hoy?: boolean }) {
  return <div className="innspel-skjelett" data-hoy={hoy ? 'true' : undefined} aria-hidden="true" />;
}
